import { BOARD_SIZE, BOX_SIZE, DIFFICULTY_CLUES, type Difficulty } from '@/game/configs';
export interface CellState {
  value: number | null;
  isGiven: boolean;
  notes: number[];
}
export type BoardState = CellState[];
export const createEmptyBoard = (): BoardState => {
  return Array.from({ length: BOARD_SIZE * BOARD_SIZE }, () => ({
    value: null,
    isGiven: false,
    notes: [],
  }));
};
const getBoxIndex = (row: number, col: number): number => {
  return Math.floor(row / BOX_SIZE) * BOX_SIZE + Math.floor(col / BOX_SIZE);
};
const getCellIndex = (row: number, col: number): number => {
  return row * BOARD_SIZE + col;
};
const shuffleArray = <T>(array: T[]): T[] => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};
const isValidPlacement = (
  board: (number | null)[],
  row: number,
  col: number,
  num: number
): boolean => {
  // Check row
  for (let c = 0; c < BOARD_SIZE; c++) {
    if (board[row * BOARD_SIZE + c] === num) return false;
  }
  // Check column
  for (let r = 0; r < BOARD_SIZE; r++) {
    if (board[r * BOARD_SIZE + col] === num) return false;
  }
  // Check 3x3 box
  const boxRow = Math.floor(row / BOX_SIZE) * BOX_SIZE;
  const boxCol = Math.floor(col / BOX_SIZE) * BOX_SIZE;
  for (let r = boxRow; r < boxRow + BOX_SIZE; r++) {
    for (let c = boxCol; c < boxCol + BOX_SIZE; c++) {
      if (board[r * BOARD_SIZE + c] === num) return false;
    }
  }
  return true;
};
const solveSudoku = (board: (number | null)[]): boolean => {
  for (let i = 0; i < BOARD_SIZE * BOARD_SIZE; i++) {
    if (board[i] === null) {
      const row = Math.floor(i / BOARD_SIZE);
      const col = i % BOARD_SIZE;
      const usedNumbers = new Set<number>();
      // Get used numbers in row
      for (let c = 0; c < BOARD_SIZE; c++) {
        const val = board[row * BOARD_SIZE + c];
        if (val !== null) usedNumbers.add(val);
      }
      // Get used numbers in column
      for (let r = 0; r < BOARD_SIZE; r++) {
        const val = board[r * BOARD_SIZE + col];
        if (val !== null) usedNumbers.add(val);
      }
      // Get used numbers in box
      const boxRow = Math.floor(row / BOX_SIZE) * BOX_SIZE;
      const boxCol = Math.floor(col / BOX_SIZE) * BOX_SIZE;
      for (let r = boxRow; r < boxRow + BOX_SIZE; r++) {
        for (let c = boxCol; c < boxCol + BOX_SIZE; c++) {
          const val = board[r * BOARD_SIZE + c];
          if (val !== null) usedNumbers.add(val);
        }
      }
      const candidates = shuffleArray(
        [1, 2, 3, 4, 5, 6, 7, 8, 9].filter((n) => !usedNumbers.has(n))
      );
      for (const num of candidates) {
        board[i] = num;
        if (solveSudoku(board)) return true;
        board[i] = null;
      }
      return false;
    }
  }
  return true;
};
const countSolutions = (board: (number | null)[], limit: number = 2): number => {
  for (let i = 0; i < BOARD_SIZE * BOARD_SIZE; i++) {
    if (board[i] === null) {
      const row = Math.floor(i / BOARD_SIZE);
      const col = i % BOARD_SIZE;
      const usedNumbers = new Set<number>();
      // Get used numbers in row
      for (let c = 0; c < BOARD_SIZE; c++) {
        const val = board[row * BOARD_SIZE + c];
        if (val !== null) usedNumbers.add(val);
      }
      // Get used numbers in column
      for (let r = 0; r < BOARD_SIZE; r++) {
        const val = board[r * BOARD_SIZE + col];
        if (val !== null) usedNumbers.add(val);
      }
      // Get used numbers in box
      const boxRow = Math.floor(row / BOX_SIZE) * BOX_SIZE;
      const boxCol = Math.floor(col / BOX_SIZE) * BOX_SIZE;
      for (let r = boxRow; r < boxRow + BOX_SIZE; r++) {
        for (let c = boxCol; c < boxCol + BOX_SIZE; c++) {
          const val = board[r * BOARD_SIZE + c];
          if (val !== null) usedNumbers.add(val);
        }
      }
      let count = 0;
      for (const num of [1, 2, 3, 4, 5, 6, 7, 8, 9]) {
        if (!usedNumbers.has(num)) {
          board[i] = num;
          count += countSolutions(board, limit - count);
          board[i] = null;
          if (count >= limit) return count;
        }
      }
      return count;
    }
  }
  return 1;
};
export const generateSolution = (): number[] => {
  const board: (number | null)[] = Array(BOARD_SIZE * BOARD_SIZE).fill(null);
  solveSudoku(board);
  return board as number[];
};
export const generatePuzzle = (difficulty: Difficulty): BoardState => {
  const solution = generateSolution();
  const board: (number | null)[] = [...solution];
  const cluesNeeded = DIFFICULTY_CLUES[difficulty];
  const cellsToRemove = BOARD_SIZE * BOARD_SIZE - cluesNeeded;
  const positions = shuffleArray(
    Array.from({ length: BOARD_SIZE * BOARD_SIZE }, (_, i) => i)
  );
  let removed = 0;
  let attempts = 0;
  const maxAttempts = BOARD_SIZE * BOARD_SIZE * 2;
  for (const pos of positions) {
    if (removed >= cellsToRemove) break;
    if (attempts >= maxAttempts) break;
    attempts++;
    const backup = board[pos];
    if (backup === null) continue;
    board[pos] = null;
    const testBoard = [...board];
    const solutionCount = countSolutions(testBoard, 2);
    if (solutionCount === 1) {
      removed++;
    } else {
      board[pos] = backup;
    }
  }
  return board.map((value) => ({
    value,
    isGiven: value !== null,
    notes: [],
  }));
};
export const validateMove = (
  board: BoardState,
  cellIndex: number,
  value: number
): boolean => {
  const row = Math.floor(cellIndex / BOARD_SIZE);
  const col = cellIndex % BOARD_SIZE;
  // Check row
  for (let c = 0; c < BOARD_SIZE; c++) {
    const idx = row * BOARD_SIZE + c;
    if (idx !== cellIndex && board[idx].value === value) return false;
  }
  // Check column
  for (let r = 0; r < BOARD_SIZE; r++) {
    const idx = r * BOARD_SIZE + col;
    if (idx !== cellIndex && board[idx].value === value) return false;
  }
  // Check 3x3 box
  const boxRow = Math.floor(row / BOX_SIZE) * BOX_SIZE;
  const boxCol = Math.floor(col / BOX_SIZE) * BOX_SIZE;
  for (let r = boxRow; r < boxRow + BOX_SIZE; r++) {
    for (let c = boxCol; c < boxCol + BOX_SIZE; c++) {
      const idx = r * BOARD_SIZE + c;
      if (idx !== cellIndex && board[idx].value === value) return false;
    }
  }
  return true;
};
export const isSolved = (board: BoardState): boolean => {
  // Check all cells are filled
  for (const cell of board) {
    if (cell.value === null) return false;
    if (cell.notes.length > 0) return false;
  }
  // Check for duplicates in rows, columns, and boxes
  for (let i = 0; i < BOARD_SIZE * BOARD_SIZE; i++) {
    const row = Math.floor(i / BOARD_SIZE);
    const col = i % BOARD_SIZE;
    const value = board[i].value!;
    // Check row
    for (let c = 0; c < BOARD_SIZE; c++) {
      const idx = row * BOARD_SIZE + c;
      if (idx !== i && board[idx].value === value) return false;
    }
    // Check column
    for (let r = 0; r < BOARD_SIZE; r++) {
      const idx = r * BOARD_SIZE + col;
      if (idx !== i && board[idx].value === value) return false;
    }
    // Check 3x3 box
    const boxRow = Math.floor(row / BOX_SIZE) * BOX_SIZE;
    const boxCol = Math.floor(col / BOX_SIZE) * BOX_SIZE;
    for (let r = boxRow; r < boxRow + BOX_SIZE; r++) {
      for (let c = boxCol; c < boxCol + BOX_SIZE; c++) {
        const idx = r * BOARD_SIZE + c;
        if (idx !== i && board[idx].value === value) return false;
      }
    }
  }
  return true;
};